import { useState } from 'react';
import CancelOutlinedIcon from '@mui/icons-material/CancelOutlined';
import CheckCircleOutlinedIcon from '@mui/icons-material/CheckCircleOutlined';
import DraftsTwoToneIcon from '@mui/icons-material/DraftsTwoTone';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import SendIcon from '@mui/icons-material/Send';
import {
  Box,
  Chip,
  ListItemIcon,
  ListItemText,
  MenuItem,
  MenuList,
  Popover,
  Tooltip
} from '@mui/material';

import { ReferralStatusEnum } from '@/types/roc/models/Referral';

import {
  type OutboundReferralActionTarget,
  useOutboundReferralActions
} from '../../hooks/useOutboundReferralActions';
import type { ReferralStatus } from '../../models/OutboundReferralModel';

const STATUS_CONFIG: Record<
  ReferralStatus,
  { label: string; Icon: React.ElementType; bgcolor: string; color: string }
> = {
  [ReferralStatusEnum.Sent]: {
    label: 'Sent',
    Icon: SendIcon,
    bgcolor: 'primary.main',
    color: 'primary.contrastText'
  },
  [ReferralStatusEnum.Confirmed]: {
    label: 'Confirmed',
    Icon: CheckCircleOutlinedIcon,
    bgcolor: 'success.main',
    color: 'success.contrastText'
  },
  [ReferralStatusEnum.Closed]: {
    label: 'Closed',
    Icon: CancelOutlinedIcon,
    bgcolor: 'error.main',
    color: 'error.contrastText'
  },
  [ReferralStatusEnum.Draft]: {
    label: 'Draft',
    Icon: DraftsTwoToneIcon,
    bgcolor: 'action.selected',
    color: 'text.primary'
  }
};

interface ReferralStatusDropdownProps {
  referral: OutboundReferralActionTarget;
}

export function ReferralStatusDropdown({
  referral
}: ReferralStatusDropdownProps) {
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const { actions, dialogElement } = useOutboundReferralActions({
    referral,
    confirmVariant: 'simple'
  });

  const confirmAction = actions.find((a) => a.key === 'confirm-referral');
  const closeAction = actions.find((a) => a.key === 'close-referral');

  const { label, Icon, bgcolor, color } =
    STATUS_CONFIG[referral.status as ReferralStatus] ??
    STATUS_CONFIG[ReferralStatusEnum.Sent];

  const handleOpen = (e: React.MouseEvent<HTMLElement>) => {
    e.stopPropagation();
    setAnchorEl(e.currentTarget);
  };

  const handleClose = () => setAnchorEl(null);

  return (
    <>
      <Chip
        label={
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: '4px',
              color
            }}
          >
            <Icon sx={{ fontSize: 15 }} />
            {label}
            <KeyboardArrowDownIcon sx={{ fontSize: 15 }} />
          </Box>
        }
        onClick={handleOpen}
        sx={{
          backgroundColor: bgcolor,
          cursor: 'pointer',
          '& .MuiChip-label': { px: 1.25 }
        }}
      />
      <Popover
        open={Boolean(anchorEl)}
        anchorEl={anchorEl}
        onClose={handleClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        onClick={(e) => e.stopPropagation()}
      >
        <MenuList sx={{ minWidth: 180, py: 0.5 }}>
          {confirmAction && (
            <Tooltip
              title={confirmAction.disabledReason ?? ''}
              placement="right"
              disableHoverListener={!confirmAction.disabled}
            >
              <span>
                <MenuItem
                  onClick={() => {
                    confirmAction.onClick();
                    handleClose();
                  }}
                  disabled={confirmAction.disabled}
                  sx={{ gap: 1 }}
                >
                  <ListItemIcon>
                    <CheckCircleOutlinedIcon fontSize="small" />
                  </ListItemIcon>
                  <ListItemText>Confirm Referral</ListItemText>
                </MenuItem>
              </span>
            </Tooltip>
          )}
          {closeAction && (
            <Tooltip
              title={closeAction.disabledReason ?? ''}
              placement="right"
              disableHoverListener={!closeAction.disabled}
            >
              <span>
                <MenuItem
                  onClick={() => {
                    closeAction.onClick();
                    handleClose();
                  }}
                  disabled={closeAction.disabled}
                  sx={{ gap: 1 }}
                >
                  <ListItemIcon>
                    <CancelOutlinedIcon fontSize="small" />
                  </ListItemIcon>
                  <ListItemText>Close Referral</ListItemText>
                </MenuItem>
              </span>
            </Tooltip>
          )}
        </MenuList>
      </Popover>
      {dialogElement}
    </>
  );
}
